import "./Settings.css";
import { useEffect, useState } from "react";
import {
  FaUserShield,
  FaInfoCircle,
  FaLock,
  FaSyncAlt,
} from "react-icons/fa";

import { db, auth } from "../../firebase/firebase";
import { collection, query, limit, getDocs } from "firebase/firestore";
import {
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";

import { useToast } from "./Invoicefiles/Usetoast";
import ToastContainer from "./Invoicefiles/Toastcontainer";

const APP_INFO = {
  name: "Sarrathi Constructions ERP",
  version: "v1.0.0",
  developedBy: "Sarrathi Constructions",
};

function Settings() {
  const { toasts, toast } = useToast();

  // ---------------- Account: change password ----------------
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);

  const resetPasswordForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError("");
  };

  const changePassword = async () => {
    setPasswordError("");

    if (!currentPassword.trim()) {
      setPasswordError("Enter your current password.");
      return;
    }

    if (!newPassword || newPassword.length < 6) {
      setPasswordError("New password must be at least 6 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New password and confirmation do not match.");
      return;
    }

    const user = auth.currentUser;
    if (!user || !user.email) {
      setPasswordError("No authenticated user found. Please log in again.");
      return;
    }

    setPasswordSaving(true);

    try {
      // Firebase requires a recent login before allowing a password
      // change, so we re-authenticate with the current password first,
      // then call the official updatePassword() method.
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);

      toast.success("Password Changed Successfully");
      resetPasswordForm();
    } catch (err) {
      console.error("Failed to change password:", err);
      const message =
        err.code === "auth/wrong-password" || err.code === "auth/invalid-credential"
          ? "Current password is incorrect."
          : err.code === "auth/weak-password"
          ? "New password is too weak. Use at least 6 characters."
          : err.code === "auth/too-many-requests"
          ? "Too many attempts. Please try again later."
          : "Failed to change password. Please try again.";
      setPasswordError(message);
      toast.error(message);
    } finally {
      setPasswordSaving(false);
    }
  };

  // ---------------- Firebase connection status ----------------
  // "checking" | "connected" | "disconnected" — never hardcoded, always
  // derived from an actual Firestore read.
  //
  // IMPORTANT: this used to read a standalone "settings/company" document
  // that nothing else in the app ever created or was granted access to
  // under Firestore Security Rules — every other page (Clients, Invoice,
  // Materials, Payments, useClientsStatusMap) only ever reads/writes
  // "clients", "estimations", "invoices", "budgetPlans",
  // "paymentAllocations", and "counters". Because no rule covered
  // "settings/company", the default-deny fired and this check reported
  // "Firebase Disconnected" even though Firebase/Firestore were working
  // correctly everywhere else in the app.
  //
  // Fixed by pointing the check at the "clients" collection instead — the
  // same collection every other page already reads successfully for an
  // authenticated admin. A minimal limit(1) query still performs a real
  // network round-trip to Firestore (so it's a legitimate connectivity
  // check), and it will only fail if Firestore/auth is genuinely down or
  // the session has genuinely lost access — never because of a probe
  // target that was never part of the app's data/security model.
  const [firebaseStatus, setFirebaseStatus] = useState("checking");

  const checkFirebaseConnection = async () => {
    setFirebaseStatus("checking");
    try {
      const clientsProbe = query(collection(db, "clients"), limit(1));
      await getDocs(clientsProbe);
      setFirebaseStatus("connected");
    } catch (err) {
      console.error("Firebase connectivity check failed:", err);
      setFirebaseStatus("disconnected");
    }
  };

  useEffect(() => {
    checkFirebaseConnection();
  }, []);

  const statusLabel =
    firebaseStatus === "connected"
      ? "🟢 Firebase Connected"
      : firebaseStatus === "disconnected"
      ? "🔴 Firebase Disconnected"
      : "🟡 Checking Connection...";

  return (
    <div className="page-container settings-page">
      <div className="page-header">
        <div>
          <h1>Settings</h1>
          <p>Manage your account and system information.</p>
        </div>
      </div>

      <div className="settings-grid">
        {/* ---------------- Account ---------------- */}
        <div className="settings-card">
          <div className="settings-card-head">
            <FaUserShield className="settings-card-icon" />
            <h2>Account</h2>
          </div>

          <div className="settings-field">
            <span className="settings-label">Logged-in Email</span>
            <span className="settings-value">{auth.currentUser?.email || "-"}</span>
          </div>

          <div className="settings-divider" />

          <h3 className="settings-subheading">
            <FaLock className="settings-subheading-icon" /> Change Password
          </h3>

          {/* Wrapped in a <form> with onSubmit + a hidden submit trigger so
              the browser stops emitting "Password field is not contained in
              a form" warnings. preventDefault keeps this a pure client-side
              action exactly as before — no page reload, no new network
              request, no change to changePassword()'s own logic. */}
          <form
            className="settings-form-grid"
            onSubmit={(e) => {
              e.preventDefault();
              changePassword();
            }}
          >
            <input
              type="password"
              placeholder="Current Password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => {
                setCurrentPassword(e.target.value);
                if (passwordError) setPasswordError("");
              }}
            />
            <input
              type="password"
              placeholder="New Password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                if (passwordError) setPasswordError("");
              }}
            />
            <input
              type="password"
              placeholder="Confirm New Password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (passwordError) setPasswordError("");
              }}
            />

            {passwordError && <span className="field-error">{passwordError}</span>}

            <div className="settings-card-actions">
              <button type="submit" className="save-btn" disabled={passwordSaving}>
                {passwordSaving ? "Updating..." : "Change Password"}
              </button>
            </div>
          </form>
        </div>

        {/* ---------------- About ---------------- */}
        <div className="settings-card">
          <div className="settings-card-head">
            <FaInfoCircle className="settings-card-icon" />
            <h2>About</h2>
          </div>

          <div className="settings-field">
            <span className="settings-label">Application Name</span>
            <span className="settings-value">{APP_INFO.name}</span>
          </div>
          <div className="settings-field">
            <span className="settings-label">Version</span>
            <span className="settings-value">{APP_INFO.version}</span>
          </div>
          <div className="settings-field">
            <span className="settings-label">Developed By</span>
            <span className="settings-value">{APP_INFO.developedBy}</span>
          </div>

          <div className="settings-divider" />

          <div className={`firebase-status-card firebase-status--${firebaseStatus}`}>
            <div>
              <span className="settings-label">Firebase Status</span>
              <span className="firebase-status-value">{statusLabel}</span>
            </div>
            <button
              className="settings-recheck-btn"
              onClick={checkFirebaseConnection}
              title="Recheck Connection"
            >
              <FaSyncAlt />
            </button>
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} onDismiss={toast.dismiss} />
    </div>
  );
}

export default Settings;